import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { OFFER_CLAIM_URL } from '../domainTypes/onboarding';
import { AuthService } from '../services/auth.service';

/**
 * Sends an already-authenticated visitor away from the sign-in surfaces.
 *
 * Apply it per route, to `login` and `signup` only — they are the sign-in
 * surfaces this guard is for. It must NOT go on the auth shell they share:
 * `passwordReset`, `verify`, `forgot-password` and `request-access` are not
 * sign-in surfaces, and a signed-in user legitimately opens any of them. Two are
 * email landings, two are forms the user fills in and submits; guarding the shell
 * redirects all four to /chat before the page renders. The test to apply to a new
 * child is "is this a sign-in surface", not "is this reached from an email".
 *
 * Verified users mid-corporate-onboarding (workspace_id null) are sent to the
 * wizard, not /chat. That branch is a shortcut, not a safety net: `authGuard` on
 * /chat runs the same `workspace_id == null` test and sends them to the same
 * wizard, so collapsing both outcomes to a single /chat redirect costs one extra
 * hop rather than stranding anyone. Keep the branch to skip the bounce; do not
 * keep it believing it is the only thing routing that user correctly.
 *
 * A signed-in user with a workspace opening a sales offer link (`?offer=`) is
 * sent to the offer claim step rather than /chat, which would drop the offer.
 */
export const redirectAuthenticatedGuard: CanActivateFn = route => {
    const auth = inject(AuthService);
    const router = inject(Router);

    if (!auth.isAuthenticated()) {
        return true;
    }

    const user = auth.state().user;
    if (user && user.workspace_id == null) {
        return router.createUrlTree(['/onboarding/setup']);
    }

    if (route.queryParamMap.has('offer')) {
        return router.parseUrl(OFFER_CLAIM_URL);
    }

    return router.createUrlTree(['/chat']);
};
